import axios from 'axios';
import { track } from '@lf/analytics-events';
import { isNoBackendMode, mockAxiosRequest } from './mockApi.js';

const apiUrl = process.env.REACT_APP_LF_API_URL || 'http://localhost:5001';
const defaultTimeout = 20000;

axios.defaults.withCredentials = true;

export class AuthorizationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AuthorizationError';
  }
}

function _attachAnalyticsInterceptor(client) {
  // Generic axios response interceptor — fires `api_error` for any
  // non-2xx response. Errors thrown by network failures are also
  // captured (status_code becomes 0). Per design §F we send the
  // route shape but never request bodies/params (privacy).
  if (!client || !client.interceptors || !client.interceptors.response) {
    return client;
  }
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const status = error?.response?.status || 0;
        const route = error?.config?.url || error?.request?.responseURL || 'unknown';
        const code = error?.response?.data?.errors?.[0]?.code
          || error?.code
          || 'unknown';
        track('api_error', { route, status_code: status, code });
      } catch (e) {
        // Never let analytics break the original error propagation.
      }
      return Promise.reject(error);
    },
  );
  return client;
}

function _createClient(timeout) {
  if (isNoBackendMode) {
    return {
      get: async (path) => await mockAxiosRequest('GET', path),
      post: async (path) => await mockAxiosRequest('POST', path),
      put: async (path) => await mockAxiosRequest('PUT', path),
      delete: async (path) => await mockAxiosRequest('DELETE', path),
    };
  }

  return _attachAnalyticsInterceptor(axios.create({
    baseURL: apiUrl,
    timeout: timeout,
    withCredentials: true,
  }));
}

export async function api_get(path, params, setError = null, timeout = defaultTimeout) {
  try {
    return await _createClient(timeout).get(path, { params: params });
  }
  catch (error) {
    console.error(error);
    if (setError && error.response && error.response.data && error.response.data.errors) {
      setError(error.response.data.errors[0]);
    }
    return null;
  }
};

export async function api_post(path, params, setError = null, timeout = defaultTimeout) {
  try {
    return await _createClient(timeout).post(path, params);
  }
  catch (error) {
    console.error(error);
    if (setError && error.response && error.response.data && error.response.data.errors) {
      setError(error.response.data.errors[0]);
    }
    return null;
  }
};

export async function api_delete(path, params, setError = null, timeout = defaultTimeout) {
  try {
    return await _createClient(timeout).delete(path, { params: params });
  }
  catch (error) {
    console.error(error);
    if (setError && error.response && error.response.data && error.response.data.errors) {
      setError(error.response.data.errors[0]);
    }
    return null;
  }
};

export async function api_put(path, params, setError = null, timeout = defaultTimeout) {
  try {
    return await _createClient(timeout).put(path, params);
  }
  catch (error) {
    console.error(error);
    if (setError && error.response && error.response.data && error.response.data.errors) {
      setError(error.response.data.errors[0]);
    }
    return null;
  }
};

export default _createClient(defaultTimeout);
